import { useCallback, useEffect, useMemo, useState } from 'react';
import { AccountTypeahead } from '../accounts/AccountTypeahead';
import { IntlPhoneField } from '../forms/IntlPhoneField';
import { Modal } from '../ui/Modal';
import { useCrmData } from '../../contexts/CrmDataContext';
import { usePortal } from '../../contexts/PortalContext';
import { useModalFormSession } from '../../hooks/useModalFormSession';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import {
  activityChangeLine,
  buildActivityChangeDetail,
  logPortalActivityQuiet,
} from '../../lib/portalActivityLog';
import { getDbSupabase } from '../../lib/supabase';
import { normalizeStoredPhone, phoneInputValue } from '../../lib/intlPhone';
import type { Contact } from '../../types/crm';
import { clientById, clientsForAccountPicker, idsMatch } from '../../utils/crmHelpers';
import { ownerIdForUser } from '../../utils/ownerId';

interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
  contact?: Contact | null;
  defaultClientId?: string;
  lockClient?: boolean;
}

export function ContactFormModal({
  open,
  onClose,
  contact,
  defaultClientId,
  lockClient = false,
}: ContactFormModalProps) {
  const { kind: portal } = usePortal();
  const { data, refresh, username, ownerIdKind, viewRole, supervisorTeam, actorSalespersonCodes, departmentSlug } =
    useCrmData();

  const selectableClients = useMemo(
    () =>
      clientsForAccountPicker(
        data.clients,
        viewRole,
        username,
        data.users,
        supervisorTeam,
        actorSalespersonCodes,
        departmentSlug,
      ),
    [data.clients, viewRole, username, data.users, supervisorTeam, actorSalespersonCodes, departmentSlug],
  );
  const isEdit = Boolean(contact?.id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [clientId, setClientId] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const sessionKey = `${contact?.id ?? 'new'}:${defaultClientId ?? ''}`;

  const initForm = useCallback(() => {
    setFirstName(contact?.first_name ?? '');
    setLastName(contact?.last_name ?? '');
    const preferred = contact?.client_id ?? defaultClientId ?? '';
    const valid =
      preferred && selectableClients.some((c) => idsMatch(c.id, preferred))
        ? preferred
        : selectableClients[0]?.id ?? '';
    setClientId(valid ? String(valid) : '');
    setJobTitle(contact?.job_title ?? '');
    setEmail(contact?.email ?? '');
    setPhone(phoneInputValue(contact?.phone));
    setError(null);
  }, [contact, defaultClientId, selectableClients]);

  useModalFormSession(open, sessionKey, initForm);

  useEffect(() => {
    if (!open || clientId) return;
    const preferred = contact?.client_id ?? defaultClientId ?? '';
    const valid =
      preferred && selectableClients.some((c) => idsMatch(c.id, preferred))
        ? String(preferred)
        : selectableClients[0]?.id
          ? String(selectableClients[0].id)
          : '';
    if (valid) setClientId(valid);
  }, [open, clientId, selectableClients, contact?.client_id, defaultClientId]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const fname = firstName.trim();
    const lname = lastName.trim();
    if (!fname || !clientId) {
      setError('First name and account are required.');
      return;
    }

    if (!clientById(selectableClients, clientId)) {
      setError('You can only link contacts to accounts you own.');
      return;
    }

    const account = clientById(data.clients, clientId);
    // Contacts stay person-owned; clients.owner_id is a salesperson code — do not resolve it as a user.
    const me = data.users.find((u) => u.username === username);
    const owner = me ? ownerIdForUser(me, ownerIdKind) : username;

    setSaving(true);
    setError(null);

    const payload = {
      first_name: fname,
      last_name: lname,
      client_id: clientId,
      job_title: jobTitle.trim(),
      email: email.trim(),
      phone: normalizeStoredPhone(phone),
      owner_id: owner,
    };

    try {
      if (isEdit && contact) {
        const { error: updateError } = await getDbSupabase().from('contacts').update(payload).eq('id', contact.id);
        if (updateError) throw updateError;
        const detail =
          buildActivityChangeDetail([
            activityChangeLine('First name', contact.first_name ?? '', fname),
            activityChangeLine('Last name', contact.last_name ?? '', lname),
            activityChangeLine('Job title', contact.job_title ?? '', payload.job_title),
            activityChangeLine('Email', contact.email ?? '', payload.email),
            activityChangeLine('Phone', contact.phone ?? '', payload.phone),
          ]) ?? `Contact "${fname} ${lname}" updated.`;
        logPortalActivityQuiet({
          portal,
          action: 'update',
          entityType: 'contact',
          entityId: contact.id,
          clientId,
          title: `Contact updated: ${fname} ${lname}`,
          detail,
        });
      } else {
        const { error: insertError } = await getDbSupabase().from('contacts').insert(payload);
        if (insertError) throw insertError;
        logPortalActivityQuiet({
          portal,
          action: 'create',
          entityType: 'contact',
          clientId,
          title: `Contact created: ${fname} ${lname}`,
          detail: `Linked to ${account?.company_name ?? 'account'} (${account?.code ?? clientId}).`,
        });
      }
      await refresh();
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit contact' : 'New contact'}
      open={open}
      onClose={onClose}
      panelClassName="modal-panel--task-form"
    >
      <form className="crm-form crm-form--modal" onSubmit={(e) => void handleSave(e)}>
        <div className="crm-form__scroll">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="crm-form__grid">
          <label className="field">
            <span className="field-label">First name *</span>
            <input className="field-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field-label">Last name</span>
            <input className="field-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </label>
        </div>

        <label className="field">
          <span className="field-label">Account *</span>
          <AccountTypeahead
            clients={selectableClients}
            value={clientId}
            onChange={setClientId}
            placeholder="Search by account name or code"
            disabled={lockClient && Boolean(defaultClientId)}
          />
        </label>

        <label className="field">
          <span className="field-label">Job title</span>
          <input className="field-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
        </label>

        <div className="crm-form__grid">
          <label className="field">
            <span className="field-label">Email</span>
            <input className="field-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Phone</span>
            <IntlPhoneField value={phone} onChange={setPhone} />
          </label>
        </div>

        </div>

        <div className="crm-form__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save contact'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
